import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import type { DeleteMediaAssetDto } from '@/types/dtos/media/request/delete-media-asset.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_CONTRIBUTOR_SUMMARY_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export const useDeleteMediaAssetMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteMediaAssetDto) => {
      const token = await getToken();
      return deleteMediaAsset(params, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] }),
        invalidateSimulationCueSheet(queryClient),
      ]);
    },
  });
};

const deleteMediaAsset = async ({ mediaId, assetVersion }: DeleteMediaAssetDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/media/${mediaId}/asset/${assetVersion}`;
  return sendRequest({ url, method: METHOD.DELETE, token });
};
